import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { Router } from '@angular/router';
import { IPageDataV3 } from '@df/catalog/cms/cms-model.interface';
import { Url } from '@df/core/service/url';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent } from '@icms/core/content';
import { LocalPage } from '../catalog/page/local/local-page';
import { NMaterialsCalculatorCms } from './materials-calculator-cms.config';
import { NMaterialsCalcConfig } from './materials-calculator.config';

/**
 * Component definition
 */
@Component({
    selector: 'materials-calculator-cms',
    templateUrl: './materials-calculator-cms.html',
    styleUrls: ['./materials-calculator-cms.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<MaterialsCalculatorCmsComponent>({
    id: NMaterialsCalculatorCms.BLOCK_ID,
    block: {
        url: NMaterialsCalculatorCms.BLOCK_ID
    },
    onDemandOnly: true,
    name: 'Materials calculator block',
    fields: NMaterialsCalculatorCms.fields
})
export class MaterialsCalculatorCmsComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NMaterialsCalculatorCms.IBlockDataContent;

    @Input()
    pageModel?: LocalPage<IPageDataV3>;

    private router = inject(Router);
    private scrollToService = inject(ScrollToService);
    protected url = inject(Url);

    protected fieldsValue = {};
    protected calculator?: NMaterialsCalcConfig.ICalculator;
    protected calculators = NMaterialsCalcConfig.calculators;
    protected EFieldType = NMaterialsCalcConfig.EFieldType;
    protected results?: NMaterialsCalcConfig.IResults;

    override ngOnChanges(changes): void {
        if (changes.pageModel && this.pageModel?.data?.typeData?.calculatorId) {
            this.calculator = this.calculators[this.pageModel?.data?.typeData?.calculatorId];
            this.fieldsValue = {};
            this.results = undefined;
            if (!this.calculator) {
                // preselect brick calc
                this.router.navigateByUrl(this.pageModel?.parent?.url || '/');
            } else {
                // preselect single image option
                this.calculator.fields
                    .filter(
                        field =>
                            field.type === NMaterialsCalcConfig.EFieldType.image &&
                            field.imageOptions?.length === 1 &&
                            field.imageOptions[0].options?.length === 1
                    )
                    .forEach(field => {
                        this.fieldsValue[field.id] = field.imageOptions?.[0].options?.[0].id;
                    });
            }
            this.detectChanges();
        }
        super.ngOnChanges(changes);
    }

    get calculatorsList() {
        return this.pageModel
            ?.getCalculatorPages()
            ?.map(
                page =>
                    page.data &&
                    this.calculators[page.data?.typeData.calculatorId] && {
                        ...this.calculators[page.data?.typeData.calculatorId],
                        url: page.url
                    }
            )
            .filter(Boolean);
    }

    protected selectImageOption(field: NMaterialsCalcConfig.IField, option: NMaterialsCalcConfig.IOption, group?: string): void {
        this.fieldsValue[field.id] = option.id;
        if (group) {
            this.fieldsValue[field.id + 'Group'] = group;
        }
        this.detectChanges();
    }

    protected isValid(): boolean {
        return !this.calculator || this.calculator.fields.every(field => !!this.fieldsValue[field.id]);
    }

    protected calculate(): void {
        if (!this.isValid()) {
            return;
        }
        switch (this.calculator?.id) {
            case 'brick': {
                const area = this.fieldsValue['width'] * this.fieldsValue['height'];
                if (this.fieldsValue['brickOrBlock'] === 'brick') {
                    this.results = {
                        materialsValue: Math.ceil(60 * area),
                        materialsNote: 'Bricks',
                        note: 'This includes a 10mm mortar joint',
                        area: area.toString(),
                        areaUnit: 'm²',
                        additionalInfo: [
                            {
                                note: 'Kg of sand',
                                value: (Math.ceil(55 * area * 100) / 100)?.toString()
                            },
                            {
                                note: 'bags of cement',
                                value: (Math.ceil(0.62 * area * 100) / 100)?.toString()
                            }
                        ]
                    };
                } else {
                    this.results = {
                        materialsValue: Math.ceil(10 * area),
                        materialsNote: 'Blocks',
                        note: 'This includes a 10mm mortar joint',
                        area: area.toString(),
                        areaUnit: 'm²',
                        additionalInfo: [
                            {
                                note: 'Kg of sand',
                                value: (Math.ceil(20.4 * area * 100) / 100)?.toString()
                            },
                            {
                                note: 'bags of cement',
                                value: (Math.ceil(0.23 * area * 100) / 100)?.toString()
                            }
                        ]
                    };
                }
                break;
            }
            case 'concrete': {
                const area = ((((this.fieldsValue['width'] / 100) * this.fieldsValue['length']) / 100) * this.fieldsValue['depth']) / 100;
                this.results = {
                    materialsValue: Math.ceil(1.96 * area * 100) / 100,
                    materialsNote: 'bulk bags of ballast',
                    area: area.toFixed(2),
                    areaUnit: 'm³',
                    additionalInfo: [
                        {
                            note: 'bags of cement (25kg)',
                            value: (Math.ceil(12.8 * area * 100) / 100)?.toString()
                        }
                    ]
                };
                break;
            }
            case 'decking': {
                const area = this.fieldsValue['width'] * this.fieldsValue['length'];
                this.results = {
                    materialsValue: Math.ceil(area * (this.fieldsValue['boardWidth'] === '125' ? 8 : 6.666666667)),
                    materialsNote: 'metres of decking',
                    area: area.toString(),
                    areaUnit: 'm²'
                };
                break;
            }
            case 'patio': {
                const area = this.fieldsValue['width'] * this.fieldsValue['length'];
                this.results = {
                    materialsValue: Math.ceil(0.1 * area * 100) / 100,
                    materialsNote: 'bulk bags of sharp sand',
                    note: 'Based on 50mm Mortar bed',
                    area: area.toString(),
                    areaUnit: 'm²',
                    additionalInfo: [
                        {
                            note: 'bags of cement',
                            value: (Math.ceil(0.32 * area * 100) / 100)?.toString()
                        }
                    ]
                };
                break;
            }
            case 'paving': {
                const selectedGroup = this.calculator.fields
                    .find(field => field.id === 'pattern')
                    ?.imageOptions?.find(option => option.group === this.fieldsValue['patternGroup']);
                const selectedPattern = selectedGroup?.options.find(option => option.id === this.fieldsValue['pattern']);

                if (!selectedPattern) {
                    return;
                }
                const area = this.fieldsValue['area'];
                this.results = {
                    materialsValue: selectedGroup?.group || '',
                    materialsNote: `(${selectedPattern.label})`,
                    note: 'Quantities are calculated per slab.',
                    area: area,
                    areaUnit: 'm²',
                    additionalInfo: selectedPattern.slabs?.map(slab => ({
                        note: slab.text,
                        value:
                            typeof selectedPattern.multiplier === 'number'
                                ? ((area / selectedPattern.multiplier) * slab.value)?.toFixed(0)
                                : ((area * (slab.additionalValue || 1)) / slab.value)?.toFixed(0)
                        // no idea why its not round upper but current logic is like this
                    }))
                };
                break;
            }
        }
        this.detectChanges();
        this.scrollToService.scrollTo('#calcResults', { offsetAdd: 80 });
    }

    protected quote(): void {
        if (this.results) {
            this.router.navigate(['get-a-quote'], { queryParams: { comments: this.parseToQuote(this.results) } });
        }
    }

    private parseToQuote(results: NMaterialsCalcConfig.IResults): string {
        const info =
            results.materialsValue +
            ' ' +
            results.materialsNote +
            (results.additionalInfo?.length ? ', ' + results.additionalInfo.map(info => info.value + ' ' + info.note).join(', ') : '');

        return (
            `Please quote me for the following required materials: ${info}, Based on an area ${results.area + ' ' + results.areaUnit}` +
            ', ' +
            (results.note ? `Note: ${results.note}` : '')
        );
    }
}
